// La VUE SESSION (R-SES-01 à R-SES-12) : leçon → pratique → rapport, ou la variante
// « faible énergie » en 20 minutes.
// La leçon théorique ouvre la séance : on lit ce qu'on va faire et pourquoi AVANT de taper
// quoi que ce soit. Les cartes de rappel, quand il y en a, viennent après elle.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMagasin, magasin } from '../donnees/magasin';
import type { BundleParcours, BundleSaison, NotionCompilee, SessionCompilee } from '@partage/contenu';
import { jourPedagogique } from '@partage/dates';
import { fileDeRevision, sessionsAujourdhui, sessionsDe } from '@partage/programme';
import { Html } from '../composants/Html';
import { RevisionCartes } from '../composants/RevisionCartes';
import { Blocage } from '../composants/Blocage';
import { Quiz } from '../composants/Quiz';
import { Chrono } from '../composants/Chrono';

type Phase = 'lecon' | 'pratique' | 'rapport';

/**
 * Une brique du soir (R-MEM-01). Elle est dépliée pendant la leçon et repliée pendant la
 * pratique : c'est là qu'on la rouvre quand la syntaxe échappe, au lieu d'aller chercher dehors.
 */
function FicheNotion({ n, repliee = false }: { n: NotionCompilee; repliee?: boolean }) {
  return (
    <details className="notion" open={!repliee}>
      <summary><code>{n.nom}</code> — <span className="doux"><Html html={n.quand_html} enLigne /></span></summary>
      <Html html={n.syntaxe_html} />
      <Html html={n.exemple_html} />
      {n.piege_html ? <p><strong>Le piège : </strong><Html html={n.piege_html} enLigne /></p> : null}
    </details>
  );
}

/**
 * Retrouve une session par son identifiant, dans les saisons ET dans les parcours annexes
 * (R-PAR-01) : une session de parcours se déroule exactement comme les autres. `voisines` est
 * la liste dont elle fait partie — elle sert à proposer la suivante à la clôture.
 */
function trouverSession(
  bundles: Record<number, BundleSaison>,
  parcours: Record<string, BundleParcours>,
  id: string,
): { voisines: SessionCompilee[]; session: SessionCompilee } | undefined {
  for (const bundle of Object.values(bundles)) {
    const voisines = sessionsDe(bundle);
    const session = voisines.find((s) => s.id === id);
    if (session) return { voisines, session };
  }
  for (const p of Object.values(parcours)) {
    const session = p.sessions.find((s) => s.id === id);
    if (session) return { voisines: p.sessions, session };
  }
  return undefined;
}

/**
 * Les débuts de phase sont gardés dans localStorage pour survivre à un rechargement.
 * Une reprise un autre soir repart de zéro : le chrono est indicatif de la séance en cours,
 * il ne court pas sur les jours où la session est restée ouverte.
 */
function useDebutsDePhase(id: string) {
  const cle = `formation:phases:${id}`;
  const [debuts, setDebuts] = useState<Partial<Record<Phase, number>>>(() => {
    try { return JSON.parse(localStorage.getItem(cle) ?? '{}'); } catch { return {}; }
  });
  const marquer = (phase: Phase) => setDebuts((d) => {
    const debut = d[phase];
    if (debut && jourPedagogique(new Date(debut)) === jourPedagogique(new Date())) return d;
    const n = { ...d, [phase]: Date.now() };
    try { localStorage.setItem(cle, JSON.stringify(n)); } catch { /* stockage indisponible */ }
    return n;
  });
  const effacer = () => { try { localStorage.removeItem(cle); } catch { /* rien */ } };
  return { debuts, marquer, effacer };
}

export function Session() {
  const { id = '' } = useParams();
  const { etat, bundles, parcours } = useMagasin();
  const naviguer = useNavigate();
  const trouve = trouverSession(bundles, parcours, id);
  const progression = etat.progression.sessions[id];
  const aujourdhui = jourPedagogique(new Date());
  const [phase, setPhase] = useState<Phase>('lecon');
  const { debuts, marquer, effacer } = useDebutsDePhase(id);
  const [texte, setTexte] = useState('');
  const [commit, setCommit] = useState(false);
  const [energie, setEnergie] = useState<number>();
  const [modeChoisi, setModeChoisi] = useState<'standard' | 'faible_energie'>(etat.parametres.faible_energie_defaut ? 'faible_energie' : 'standard');
  const [erreur, setErreur] = useState<string>();

  const fileRappel = useMemo(() => fileDeRevision(etat, aujourdhui).slice(0, modeChoisi === 'faible_energie' ? 10 : 15), [etat, aujourdhui, modeChoisi]);
  const variante = etat.variantes.find((v) => v.pour_session === id && v.statut === 'planifiee');
  const exerciceVariante = variante ? Object.values(bundles).flatMap(sessionsDe).find((s) => s.exercice.id === variante.exercice)?.exercice : undefined;

  useEffect(() => { if (progression?.statut === 'en_cours') marquer(phase); }, [phase, progression?.statut]);

  if (!trouve) return <div className="carte"><h2>Session {id} introuvable</h2><p>Le contenu de sa saison n’est peut-être pas encore téléchargé. <Link to="/">Retour au tableau de bord</Link></p></div>;
  const { voisines, session } = trouve;
  const terminee = progression?.statut === 'terminee';
  const enCours = progression?.statut === 'en_cours';
  const prochaine = voisines.find((s) => s.numero > session.numero && etat.progression.sessions[s.id]?.statut !== 'terminee');

  async function commencer() {
    await magasin.emettre('session_commencee', { session: id, mode: modeChoisi, titre_exercice: session.exercice.titre });
    marquer('lecon');
  }

  async function terminerStandard() {
    setErreur(undefined);
    if (texte.trim().length < 10) { setErreur('Écris au moins une phrase sur ce que tu as fait ce soir (10 caractères minimum).'); return; }
    try {
      await magasin.emettre('session_terminee', { session: id, mode: 'standard', energie, texte: texte.trim(), commit_fait: commit, cartes: session.cartes, entree_journal: crypto.randomUUID() });
      if (variante) await magasin.emettre('variante_terminee', { exercice: variante.exercice, session: id });
    } catch (e) {
      setErreur(`La session n’a pas pu être enregistrée : ${(e as Error).message}`);
      return;
    }
    effacer();
    naviguer('/');
  }

  async function terminerFaibleEnergie() {
    setErreur(undefined);
    if (texte.trim().length < 5) { setErreur('Une ligne suffit, mais il en faut une (5 caractères minimum).'); return; }
    try {
      await magasin.emettre('session_faible_energie_terminee', { session: id, texte: texte.trim(), entree_journal: crypto.randomUUID() });
    } catch (e) {
      setErreur(`La session n’a pas pu être enregistrée : ${(e as Error).message}`);
      return;
    }
    effacer();
    naviguer('/');
  }

  const dejaAujourdhui = sessionsAujourdhui(etat, aujourdhui);

  // ---- En-tête commun ----
  const entete = (
    <div className="carte session-suivante">
      <div className="ligne">
        <span className="badge accent">{session.id}</span>
        <span className="badge">Semaine {session.semaine}</span>
        {session.domaines.map((d) => <span className="badge" key={d}>{d}</span>)}
        {terminee ? <span className="badge ok">Terminée</span> : enCours ? <span className="badge accent">En cours · {progression?.mode === 'faible_energie' ? '20 min' : `${session.duree} min`}</span> : null}
      </div>
      <h2 style={{ marginTop: '0.5em' }}>{session.titre}</h2>
      <p className="objectif"><strong>Ce soir tu vas faire marcher :</strong> {session.resultat_visible}</p>
      {enCours || terminee
        ? <details className="apercu-seance"><summary className="petit-texte doux">Pourquoi cette séance</summary><Html html={session.html} /></details>
        : <Html html={session.html} />}
      {!enCours && !terminee ? (
        <div className="ligne">
          <select value={modeChoisi} onChange={(e) => setModeChoisi(e.target.value as 'standard' | 'faible_energie')} style={{ width: 'auto' }}>
            <option value="standard">Standard · {session.duree} min</option>
            <option value="faible_energie">Faible énergie · 20 min</option>
          </select>
          <button className="principal" onClick={() => void commencer()}>{progression?.statut === 'abandonnee' ? 'Reprendre' : 'Commencer'}</button>
        </div>
      ) : null}
    </div>
  );

  if (!enCours) {
    return (
      <>
        {entete}
        {terminee ? <div className="carte"><p>Session terminée le {new Date(progression!.terminee_le!).toLocaleDateString('fr-FR')}. Tu peux relire les capsules et l’exercice ci-dessous.</p></div> : null}
        <div className="carte"><h3>Aperçu des étapes</h3><ol>{session.etapes.map((e, i) => <li key={i}>{e.titre}</li>)}</ol></div>
        {terminee ? <div className="carte"><h3>Exercice — {session.exercice.titre}</h3><Html html={session.exercice.sections.enonce} /><details><summary>Solution (palier 4)</summary><Html html={session.exercice.sections.palier_4} /></details></div> : null}
      </>
    );
  }

  // ---- Variante faible énergie (R-SES-10) ----
  if (progression?.mode === 'faible_energie') {
    const fe = session.faible_energie;
    return (
      <>
        {entete}
        <div className="phases">
          <section className="phase active">
            <h2>1. Cartes <Chrono depuis={debuts.lecon} cibleMin={5} /></h2>
            <RevisionCartes file={fileRappel} titre="Rappel" />
          </section>
          <section className="phase active">
            <h2>2. Lecture commentée — {fe.lecture_commentee.titre} <span className="chrono">{fe.lecture_commentee.minutes} min</span></h2>
            <div className="carte"><Html html={fe.lecture_commentee.html} /></div>
          </section>
          <section className="phase active">
            <h2>3. Quiz</h2>
            <div className="carte"><Quiz questions={fe.quiz} /></div>
          </section>
          {fe.diff_html ? <section className="phase active"><h2>4. Diff à relire</h2><div className="carte"><Html html={fe.diff_html} /></div></section> : null}
          <section className="phase active">
            <h2>Clôture</h2>
            <div className="carte">
              {erreur ? <div className="message erreur">{erreur}</div> : null}
              <div className="champ"><label htmlFor="texte">Une ligne pour ton journal</label><input id="texte" value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Ex. Cartes et quiz faits, fatigué mais présent." /></div>
              <button className="principal" onClick={() => void terminerFaibleEnergie()}>Terminer la session faible énergie</button>
              <p className="petit-texte doux" style={{ marginTop: '0.6em' }}>Compte pour ta régularité. La session {session.id} reste ta session suivante, pour le soir où tu auras l’énergie de construire.</p>
            </div>
          </section>
        </div>
      </>
    );
  }

  // ---- Session standard ----
  const etapesFaites = progression?.etapes_faites ?? [];
  const classePhase = (p: Phase) => `phase ${phase === p ? 'active' : ''}`;
  return (
    <>
      {entete}
      <div className="phases">
        <section className={classePhase('lecon')} id="lecon">
          <h2><button className={phase === 'lecon' ? 'petit principal' : 'petit'} onClick={() => setPhase('lecon')}>1</button> Leçon <Chrono depuis={debuts.lecon} cibleMin={session.lecon.minutes} /></h2>
          {phase === 'lecon' ? (
            <>
              <div className="carte lecon">
                <h3>{session.lecon.titre} <span className="badge">{session.lecon.minutes} min</span></h3>
                <Html html={session.lecon.html} />
              </div>
              {session.notions.length ? (
                <div className="carte briques">
                  <h3>Les briques de ce soir</h3>
                  <p className="doux">
                    Tout ce que les étapes et l’exercice vont utiliser. Elles restent dépliables
                    pendant la pratique, et rejoignent ton mémo à la fin de la séance.
                  </p>
                  {session.notions.map((n) => <FicheNotion n={n} key={n.id} />)}
                </div>
              ) : null}
              {fileRappel.length ? (
                <div className="carte">
                  <h3>Rappel — {fileRappel.length} carte(s) des séances précédentes</h3>
                  <RevisionCartes file={fileRappel} titre="Rappel" />
                </div>
              ) : null}
              <div className="ligne"><button className="principal" onClick={() => setPhase('pratique')}>J’ai lu, passer à la pratique</button></div>
            </>
          ) : <p className="doux petit-texte">{session.lecon.titre} · {session.lecon.minutes} min{fileRappel.length ? ` · ${fileRappel.length} carte(s) à réviser` : ''}</p>}
        </section>

        <section className={classePhase('pratique')} id="pratique">
          <h2><button className={phase === 'pratique' ? 'petit principal' : 'petit'} onClick={() => setPhase('pratique')}>2</button> Pratique <Chrono depuis={debuts.pratique} cibleMin={35} /></h2>
          {phase === 'pratique' ? (
            <>
              {session.notions.length ? (
                <details className="carte">
                  <summary>Les briques de ce soir ({session.notions.length})</summary>
                  {session.notions.map((n) => <FicheNotion n={n} repliee key={n.id} />)}
                </details>
              ) : null}
              {exerciceVariante ? (
                <div className="carte" style={{ borderColor: 'var(--attention)' }}>
                  <h3>Variante à refaire seul — {exerciceVariante.titre} <span className="badge attention">R-BLQ-06</span></h3>
                  <Html html={exerciceVariante.sections.variante} />
                </div>
              ) : null}
              <div className="carte">
                <h3>Étapes</h3>
                {session.etapes.map((e, i) => {
                  const faite = etapesFaites.includes(i);
                  return (
                    <div className={`etape ${faite ? 'faite' : ''}`} key={i}>
                      <input type="checkbox" checked={faite} aria-label={`Étape ${i + 1} : ${e.titre}`} onChange={() => void magasin.emettre(faite ? 'etape_reouverte' : 'etape_terminee', { session: id, index: i })} />
                      <div>
                        <div className="titre-etape"><strong>{i + 1}. {e.titre}</strong></div>
                        <Html html={e.consigne_html} />
                        <div className="resultat">✔ Résultat attendu : {e.resultat}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {session.capsules.length ? (
                <div className="carte">
                  <h3>Capsules (juste à temps, 3 à 5 min)</h3>
                  {session.capsules.map((c) => (
                    <details className="capsule" key={c.id} onToggle={(e) => { if ((e.target as HTMLDetailsElement).open && !progression?.capsules_lues.includes(c.id)) void magasin.emettre('capsule_lue', { session: id, capsule: c.id }); }}>
                      <summary>{c.titre} <span className="badge">{c.minutes} min</span>{progression?.capsules_lues.includes(c.id) ? <span className="badge ok">lue</span> : null}</summary>
                      <div className="corps"><Html html={c.html} /></div>
                    </details>
                  ))}
                </div>
              ) : null}
              <div className="carte">
                <h3>Exercice — {session.exercice.titre}</h3>
                <Html html={session.exercice.sections.enonce} />
                <details><summary>Résultat attendu</summary><Html html={session.exercice.sections.resultat_attendu} /></details>
                {session.exercice.tests ? <p className="petit-texte">Vérification : <code>{session.exercice.tests}</code></p> : null}
                <Blocage exercice={session.exercice} session={id} prochaineSession={prochaine?.id} />
              </div>
              <div className="ligne"><button className="principal" onClick={() => setPhase('rapport')}>Passer au rapport</button></div>
            </>
          ) : <p className="doux petit-texte">{etapesFaites.length} / {session.etapes.length} étapes faites.</p>}
        </section>

        <section className={classePhase('rapport')} id="rapport">
          <h2><button className={phase === 'rapport' ? 'petit principal' : 'petit'} onClick={() => setPhase('rapport')}>3</button> Rapport <Chrono depuis={debuts.rapport} cibleMin={10} /></h2>
          {phase === 'rapport' ? (
            <div className="carte">
              <ol className="petit-texte doux">
                <li>Explique au mentor ce que tu as fait (<code>/session</code> dans Claude Code, partie clôture).</li>
                <li>Commit : {session.commit_attendu ? <code>git commit -m "{session.commit_attendu}"</code> : 'un message clair au présent.'}</li>
                <li>Une ligne de journal ci-dessous.</li>
              </ol>
              {erreur ? <div className="message erreur">{erreur}</div> : null}
              <div className="champ">
                <label htmlFor="texte">Ce que j’ai fait ce soir</label>
                <textarea id="texte" rows={3} value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Ce qui marche, ce qui a résisté, ce que j’ai compris." />
              </div>
              <div className="ligne">
                <label style={{ fontWeight: 400 }}><input type="checkbox" checked={commit} onChange={(e) => setCommit(e.target.checked)} />Commit fait</label>
                <label style={{ fontWeight: 400 }}>Énergie :
                  {[1, 2, 3, 4, 5].map((n) => <button key={n} className={`petit ${energie === n ? 'principal' : ''}`} style={{ marginLeft: 4 }} onClick={() => setEnergie(n)}>{n}</button>)}
                </label>
              </div>
              {dejaAujourdhui >= 2 ? <div className="message info">Deux sessions aujourd’hui : la mémoire aime les nuits entre deux ; tu peux continuer si tu es en forme.</div> : null}
              <button className="principal" onClick={() => void terminerStandard()}>Terminer la session</button>
            </div>
          ) : null}
        </section>
      </div>
    </>
  );
}
